import { useState, useEffect, useRef, useCallback } from 'react';
import apiService from '../services/apiService';

export const useApi = (url, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const {
    method = 'GET',
    params = null,
    body = null,
    headers = {},
    enabled = true
  } = options;

  const fetchData = useCallback(async () => {
    if (!url || !enabled) return;

    try {
      setLoading(true);
      setError(null);

      const result = await apiService.request(url, {
        method,
        params,
        body,
        headers,
      });

      if (mountedRef.current) {
        setData(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [
    url,
    method,
    enabled,
    JSON.stringify(params),
    JSON.stringify(body),
    JSON.stringify(headers)
  ]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
};

export const useApiSubmit = (options = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const {
    onSuccess,
    onError,
    successTimeout = 5000,
  } = options;

  const submit = useCallback(async (url, submitOptions = {}) => {
    if (!url) return;

    const {
      method = 'POST',
      headers = {},
      body = null,
      params = null,
    } = submitOptions;

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const result = await apiService.request(url, {
        method,
        headers,
        body,
        params,
      });

      setLoading(false);
      setSuccess(true);

      onSuccess?.(result);

      if (successTimeout > 0) {
        setTimeout(() => {
          setSuccess(false);
        }, successTimeout);
      }

      return result;
    } catch (err) {
      setLoading(false);
      setError(err.message);
      onError?.(err);
      throw err;
    }
  }, [onSuccess, onError, successTimeout]);

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return {
    submit,
    loading,
    error,
    success,
    reset
  };
};
